import React, { Fragment, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, ArrowRightIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../components/LanguageContext';
import { IdentityArchitecture } from '../components/identity/IdentityArchitecture';
import { TrustFoundation } from '../components/identity/TrustFoundation';
import { CompoundingFoundation } from '../components/identity/CompoundingFoundation';
import { identityHero } from '../data/identityArchitecture';
import { trackEvent as track } from '../utils/analytics';
// FR translations for "The foundation. Not a feature." comparison columns.
const TRADITIONAL_FR: Record<string, string> = {
  'Separate logins': 'Connexions séparées',
  'Multiple records': 'Dossiers multiples',
  'Duplicate data': 'Données dupliquées',
  'Fragmented services': 'Services fragmentés',
  'Higher operational costs': 'Coûts opérationnels plus élevés'
};
const FOUNDATION_FR: Record<string, string> = {
  'One verified identity': 'Une identité vérifiée',
  'One verified profile': 'Un profil vérifié',
  'Shared trust layer': 'Couche de confiance partagée',
  'Connected services': 'Services connectés',
  'Lower operational complexity': 'Complexité opérationnelle réduite'
};
// FR translations for the compounding-effect stack items.
// "Communications", "Services", "Analytics" and "ATNA" are intentionally kept as-is.
const STACK_FR: Record<string, string> = {
  Identity: 'Identité'
};
// FR translations for the identity-layer cards.
const CARDS_FR: Record<string, {
  title: string;
  desc: string;
}> = {
  'Digital Identity': {
    title: 'Identité numérique',
    desc: 'Profils et identifiants vérifiés.'
  },
  'Single Sign-On (SSO)': {
    title: 'Authentification unique (SSO)',
    desc: 'Une connexion sécurisée unique pour tous les services connectés.'
  },
  'Multi-factor authentication (MFA)': {
    title: 'Authentification multifacteur (AMF)',
    desc: 'Sécurité de calibre professionnel pour chaque compte.'
  },
  'Consent & Audit Trail (Law 25)': {
    title: 'Consentement et piste d’audit (Loi 25)',
    desc: 'Protection des renseignements personnels et gouvernance des données intégrées.'
  },
  'Open APIs': {
    title: 'API ouvertes',
    desc: 'Connectez les systèmes existants à la couche d’identité.'
  },
  'Identity Governance': {
    title: 'Gouvernance de l’identité',
    desc: 'Contrôle centralisé des accès et des permissions.'
  },
  'Profile Management': {
    title: 'Gestion des profils',
    desc: 'Des outils en libre-service pour gérer ses données.'
  },
  'Trusted Data Layer': {
    title: 'Couche de données de confiance',
    desc: 'La fondation de données propres requise pour l’IA.'
  }
};
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
const INCLUDED_CARDS = [{
  title: 'Digital Identity',
  desc: 'Verified profiles and credentials.'
}, {
  title: 'Single Sign-On (SSO)',
  desc: 'One secure login across all connected services.'
}, {
  title: 'Multi-factor authentication (MFA)',
  desc: 'Enterprise-grade security for every account.'
}, {
  title: 'Consent & Audit Trail (Law 25)',
  desc: 'Built-in privacy compliance and data governance.'
}, {
  title: 'Open APIs',
  desc: 'Connect existing systems to the identity layer.'
}, {
  title: 'Identity Governance',
  desc: 'Centralized control over access and permissions.'
}, {
  title: 'Profile Management',
  desc: 'Self-service tools for people to manage their data.'
}, {
  title: 'Trusted Data Layer',
  desc: 'The clean data foundation required for AI.'
}];
export function CoreInfrastructure() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const tradLabel = (label: string) => lang === 'FR' ? TRADITIONAL_FR[label] ?? label : label;
  const foundationLabel = (label: string) => lang === 'FR' ? FOUNDATION_FR[label] ?? label : label;
  const stackLabel = (label: string) => lang === 'FR' ? STACK_FR[label] ?? label : label;
  const yearLabel = (label: string) => lang === 'FR' ? label.replace(/^Year\b/, 'An') : label;
  const cardTitle = (title: string) => lang === 'FR' ? CARDS_FR[title]?.title ?? title : title;
  const cardDesc = (title: string, desc: string) => lang === 'FR' ? CARDS_FR[title]?.desc ?? desc : desc;
  useEffect(() => {
    track('Page Viewed', {
      page: 'Core Infrastructure'
    });
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-8268" data-mp="30c8rk28y8cva">
      <Header data-id="element-8269" data-mp="2wci4o9mjmgw1" />

      <main className="pt-[120px]" data-id="element-8270" data-mp="2wczzr2xgyp2d">
        {/* HERO SECTION */}
        <section className="pt-16 pb-24 px-6" data-id="element-8271" data-mp="2wekwmz79ak3e">
          <div className="max-w-[1320px] mx-auto grid grid-cols-1 lg:grid-cols-[minmax(0,0.82fr)_minmax(0,1.18fr)] gap-12 lg:gap-16 items-center" data-id="element-8272" data-mp="1ozpxl17g2zvh">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} data-id="element-8273" data-mp="1kzfohfocvtce">
              <div className="font-sans text-xs font-semibold tracking-[0.2em] text-indigo uppercase mb-6" data-id="element-8274" data-mp="9fp5b70n5o9l">
                {isFR ? identityHero.eyebrow.fr.toUpperCase() : identityHero.eyebrow.en.toUpperCase()}
              </div>
              <h1 className="font-serif text-5xl md:text-[3.4rem] text-[#121212] leading-[1.08] tracking-tight mb-8" data-id="element-8275" data-mp="j4gvb9m55yhv">
                {identityHero.headline.map(line => <span key={line.en} className="block" data-id="element-8276" data-mp="o6g0mp1fz5hg">
                    {isFR ? line.fr : line.en}
                  </span>)}
              </h1>
              <p className="font-sans text-lg text-[#121212]/70 leading-relaxed mb-5 max-w-xl" data-id="element-8277" data-mp="x48hvyqq4n7h">
                {isFR ? identityHero.body[0].fr : identityHero.body[0].en}
              </p>
              <p className="font-sans text-lg text-[#121212]/60 leading-relaxed mb-10 max-w-xl" data-id="element-8278" data-mp="2yvv0bbwjv9sc">
                {isFR ? identityHero.body[1].fr : identityHero.body[1].en}
              </p>
              <div className="flex flex-wrap items-center gap-4" data-id="element-8279" data-mp="13mi9drob6qx">
                <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
                location: 'Core Hero'
              })} className="inline-flex items-center gap-2 px-7 py-3.5 bg-indigo text-white text-base font-medium rounded-lg hover:bg-indigo/90 transition-colors duration-200" data-id="element-8280" data-mp="15fquyluk11k">
                  {isFR ? identityHero.primaryCta.fr : identityHero.primaryCta.en}
                  <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-8281" data-mp="k1p5bn0nutm3" />
                </Link>
                <Link to="/brossard" className="inline-flex items-center gap-2 px-7 py-3.5 border border-[#E4E6EC] rounded-lg font-sans text-base font-medium text-[#121212] hover:border-[#121212] transition-colors duration-200" data-id="element-8282" data-mp="xskdsy78z8p5">
                  {isFR ? identityHero.secondaryCta.fr : identityHero.secondaryCta.en}
                  <ArrowRightIcon className="w-4 h-4" data-id="element-8283" data-mp="12qlw3gwun3zk" />
                </Link>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="relative" data-id="element-8284" data-mp="1rd1ppjbr1u5i">
              {isFR ? <img src="https://cdn.magicpatterns.com/uploads/p9SdprrJs82Ae2VUqp8Jvu/ChatGPT_Image_19_sept._2026,_17_04_05.png" alt="Une identité de confiance — profil, relations, droits, consentements et attestations — reliée aux communications, services, transactions et à l’intelligence ATNA, puis à la couche API et orchestration et aux systèmes externes." className="w-full h-auto object-contain" data-id="element-8285" data-mp="1hh8hgc52cmtj" /> : <img src="https://cdn.magicpatterns.com/uploads/8vEgLXSVJWHZtL2AnbLcsx/ChatGPT_Image_19_sept._2026,_17_05_32.png" alt="A trusted identity — profile, relationships, rights, consents and credentials — connected to communications, services, transactions and ATNA intelligence, then through API and orchestration to external systems." className="w-full h-auto object-contain" data-id="element-8286" data-mp="yoxk0xt6hh2t" />}
            </motion.div>
          </div>
        </section>

        {/* SECTION 2: THE TRUST FOUNDATION — what identity actually contains */}
        <section className="py-24 lg:py-28 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-8287" data-mp="q8qgp4nwe7mi">
          <TrustFoundation data-id="element-8288" data-mp="qb2tz4ipntt8" />
        </section>

        {/* SECTION 3: A FOUNDATION THAT COMPOUNDS — deploy once, add capabilities */}
        <section className="py-24 lg:py-28 px-6 border-t border-[#E4E6EC] overflow-hidden" data-id="element-8289" data-mp="hfvao8qlw7vo">
          <CompoundingFoundation data-id="element-8290" data-mp="cgscn891n1x7" />
        </section>

        {/* SECTION 4: PROOF STRIP — already deployed, already used */}
        <section className="py-16 lg:py-20 px-6 border-t border-[#E4E6EC]" data-id="element-8291" data-mp="7ja4vl6risqs">
          <motion.div {...reveal} className="max-w-[1320px] mx-auto" data-id="element-8292" data-mp="2jnnr0ntcscp">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8" data-id="element-8293" data-mp="2kyv1aggwi8k">
              <div data-id="element-8294" data-mp="3upasxkpkc2tr">
                <p className="font-sans text-xs font-semibold tracking-[0.22em] text-[#121212] uppercase" data-id="element-8295" data-mp="2a55wmzz1foyj">
                  {isFR ? 'Éprouvé en production' : 'Proven in production'}
                </p>
                <h2 className="font-serif text-3xl md:text-4xl text-[#121212] leading-tight tracking-tight mt-4" data-id="element-8296" data-mp="2j52h0peea1ns">
                  {isFR ? 'Déployé. Utilisé. Éprouvé.' : 'Deployed. Used. Proven.'}
                </h2>

                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-6" data-id="element-8297" data-mp="1qhy7q889r167">
                  {[isFR ? 'Brossard' : 'Brossard', isFR ? 'En production depuis 2017' : 'In production since 2017', isFR ? '88 266 usagers actifs' : '88,266 active users', isFR ? 'Plus de 20 communautés' : '20+ communities'].map((item, index) => <Fragment key={item} data-id="element-8298" data-mp="256kwf0jsd5a3" data-mpi={index}>
                      {index > 0 && <span className="w-px h-4 bg-[#E4E6EC]" aria-hidden="true" data-id="element-8299" data-mp="17qovvsb100bg" />}
                      <span className="font-sans text-base text-[#121212]" data-id="element-8300" data-mp="17oksdbh1h3bp">
                        {item}
                      </span>
                    </Fragment>)}
                </div>

                <p className="font-sans text-sm text-[#121212]/50 mt-3" data-id="element-8301" data-mp="1twz305ikxp1l">
                  {isFR ? 'Loi 25 · Infrastructure hébergée au Canada' : 'Law 25 · Canadian-hosted infrastructure'}
                </p>
              </div>

              <Link to="/brossard" className="inline-flex items-center gap-2 font-sans text-[15px] font-medium text-indigo underline underline-offset-4 decoration-indigo/40 hover:decoration-indigo transition-colors duration-200 shrink-0" data-id="element-8302" data-mp="28rvin3spxeop">
                {isFR ? 'Voir l’étude de cas de Brossard' : 'Explore the Brossard case study'}
                <ArrowRightIcon className="w-4 h-4" strokeWidth={1.75} data-id="element-8303" data-mp="1l1su2xo0iquu" />
              </Link>
            </div>
          </motion.div>
        </section>

        {/* SECTION 7: FINAL CTA */}
        <section className="py-40 px-6 border-t border-[#E4E6EC]" data-id="element-8304" data-mp="scuadw48rgnf">
          <div className="max-w-3xl mx-auto text-center" data-id="element-8305" data-mp="xcqerwgupa3i">
            <motion.h2 {...reveal} className="font-serif text-5xl text-[#121212] mb-8" data-id="element-8306" data-mp="xb5ju5qmjp54">
              {lang === 'FR' ? 'Bâtissez sur une fondation de confiance.' : 'Build on a trusted foundation.'}
            </motion.h2>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="mb-12" data-id="element-8307" data-mp="36jtuk4whwg1a">
              <p className="font-sans text-xl text-[#121212]/60 mb-2" data-id="element-8308" data-mp="2wo09ty8mhg8f">

              </p>
              <p className="font-sans text-xl text-[#121212] font-medium" data-id="element-8309" data-mp="2ro5kw08er0q0">
                {lang === 'FR' ? 'Tout commence par l’identité.' : 'Everything starts with identity.'}
              </p>
            </motion.div>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} data-id="element-8310" data-mp="2ivaj5gi5fn4z">
              <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
              location: 'Core Final'
            })} className="inline-flex items-center gap-2 px-8 py-4 bg-indigo text-white text-base font-medium rounded-lg hover:bg-indigo/90 transition-colors" data-id="element-8311" data-mp="2dyadh6p7omee">
                {lang === 'FR' ? 'Demander une démo' : 'Request a demo'}
                <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-8312" data-mp="1za0mxp7ut8ew" />
              </Link>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer data-id="element-8313" data-mp="1lcc2gihp83ad" />
    </div>;
}